import { MapMultiMarker } from 'const/Map/MapMultiMarker';
import { flatten } from 'lodash';
import {
  EventData,
  GeoJSONSource,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  Popup,
} from 'mapbox-gl';
import { GeoOrders } from 'pages/planner/map/utils/mapUtils';
import { PlannerOrder, WindowGroups } from 'pages/planner/utils/usePlannerData';
import React, { useEffect } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { MapMarkerPopup } from '../MarkerPopup/MarkerPopup';

interface MultiMarkerProps {
  map: Map;
  rightClickPopupRef: Popup;
  multiMarkers: GeoOrders;
  selectedOrders: PlannerOrder[];
  selectedOrderIds: string[];
  windowGroups: WindowGroups;
  windows: [string, string][];
  fetchedBundles: BundleJson[];
  onSelect: (orders: PlannerOrder[]) => void;
}

export const MultiMarker = ({
  map,
  rightClickPopupRef,
  onSelect,
  ...props
}: MultiMarkerProps) => {
  /* assignLayers */
  /* Create the Layers for the Orders Marker */
  useEffect(() => {
    /* Wrapper */
    if (map.getSource(MapMultiMarker.SourceId)) {
      (map.getSource(MapMultiMarker.SourceId) as GeoJSONSource).setData(
        props.multiMarkers
      );
    } else {
      map.addSource(MapMultiMarker.SourceId, {
        type: 'geojson',
        data: props.multiMarkers,
      });
    }

    /* Remove existing layers */
    map.getLayer(MapMultiMarker.PointId) &&
      map.removeLayer(MapMultiMarker.PointId);
    map.getLayer(MapMultiMarker.LabelId) &&
      map.removeLayer(MapMultiMarker.LabelId);

    /* Assigning layers */
    if (props.multiMarkers.features.length) {
      map.addLayer({
        id: MapMultiMarker.PointId,
        type: 'circle',
        source: MapMultiMarker.SourceId,
        minzoom: 12,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'window'],
            ...flatten(props.windows),
            '#661aff',
          ],
          'circle-radius': 10,
          'circle-stroke-width': 5,
          'circle-stroke-color': ['get', 'markerColor'],
        },
      });

      map.addLayer({
        id: MapMultiMarker.LabelId,
        type: 'symbol',
        source: MapMultiMarker.SourceId,
        minzoom: 12,
        filter: ['>', 'numberOfOrders', 1],
        layout: {
          'text-field': '{numberOfOrders}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });
    }
  }, [map, props.multiMarkers, props.windows]);

  /* rightClickOnMarker */
  useEffect(() => {
    const action = (
      e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      MapMarkerPopup(
        map,
        e.features,
        e.lngLat,
        rightClickPopupRef,
        props.selectedOrderIds,
        props.windowGroups,
        props.fetchedBundles,
        onSelect,
        'Edit'
      );
    };

    map.on('contextmenu', MapMultiMarker.PointId, action);

    return () => {
      map.off('contextmenu', MapMultiMarker.PointId, action);
    };
  }, [
    map,
    onSelect,
    props.fetchedBundles,
    props.windowGroups,
    rightClickPopupRef,
    props.selectedOrderIds,
  ]);

  return <></>;
};
